import React from "react";
import { FileText, MessageSquare, Calendar, User, Building2, CheckCircle, XCircle, Clock, Download, Eye } from "lucide-react";
import type { TaskSubmission } from "../../../types/task";

interface TaskSubmissionCardProps {
  submission: TaskSubmission;
  onApprove?: (submission: TaskSubmission) => void;
  onReject?: (submission: TaskSubmission) => void;
  onView?: (submission: TaskSubmission) => void;
  canReview?: boolean;
}

export const TaskSubmissionCard: React.FC<TaskSubmissionCardProps> = ({ submission, onApprove, onReject, onView, canReview = false }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return "Bilinmiyor";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const getStatusBadge = () => {
    switch (submission.status) {
      case "APPROVED":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-green-500 to-green-600 text-white shadow-sm">
            <CheckCircle className="w-3 h-3" />
            Onaylandı
          </span>
        );
      case "REJECTED":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-red-500 to-red-600 text-white shadow-sm">
            <XCircle className="w-3 h-3" />
            Reddedildi
          </span>
        );
      case "SUBMITTED":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-sm">
            <Clock className="w-3 h-3" />
            İncelemede
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-gray-400 to-gray-500 text-white shadow-sm">
            <Clock className="w-3 h-3" />
            Bekliyor
          </span>
        );
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-all duration-200">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex-shrink-0">
            <div className="h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
              {submission.fileUrl ? (
                <FileText className="w-5 h-5 text-indigo-600" />
              ) : (
                <MessageSquare className="w-5 h-5 text-indigo-600" />
              )}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-gray-900 truncate">{submission.taskTitle}</h4>
            <p className="text-xs text-gray-500 mt-0.5">Görev Gönderimi</p>
          </div>
        </div>
        {getStatusBadge()}
      </div>

      <div className="mb-4 space-y-2">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="font-medium text-gray-900">{submission.userName}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span>{submission.userDepartment}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span>{formatDate(submission.submittedAt)}</span>
        </div>
      </div>

      {submission.fileUrl ? (
        <div className="mb-4 p-3 bg-gray-50 rounded-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <FileText className="w-4 h-4 text-purple-600 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">{submission.fileName || "dosya.pdf"}</p>
                <p className="text-xs text-gray-500">{formatFileSize(submission.fileSize)}</p>
              </div>
            </div>
            <button
              onClick={() => {
                window.open(submission.fileUrl, "_blank");
              }}
              className="ml-2 p-2 rounded-md hover:bg-gray-200 text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0"
              title="İndir">
              <Download className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        <div className="mb-4 p-3 bg-gray-50 rounded-md border border-gray-200">
          <p className="text-sm text-gray-700 line-clamp-3">{submission.content || "İçerik bulunamadı"}</p>
          {submission.content && submission.content.length > 150 && (
            <button
              onClick={() => onView?.(submission)}
              className="mt-2 text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
              <Eye className="w-3 h-3" />
              Tamamını Görüntüle
            </button>
          )}
        </div>
      )}

      {submission.reviewNote && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
          <p className="text-xs font-medium text-amber-900 mb-1">İnceleme Notu:</p>
          <p className="text-sm text-amber-800">{submission.reviewNote}</p>
        </div>
      )}

      {canReview && submission.status === "SUBMITTED" && (onApprove || onReject) && (
        <div className="flex gap-2 pt-4 border-t border-gray-100">
          {onApprove && (
            <button
              onClick={() => onApprove(submission)}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition-colors">
              <CheckCircle className="w-4 h-4" />
              Onayla
            </button>
          )}
          {onReject && (
            <button
              onClick={() => onReject(submission)}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors">
              <XCircle className="w-4 h-4" />
              Reddet
            </button>
          )}
        </div>
      )}

      {(submission.status === "APPROVED" || submission.status === "REJECTED") && onView && (
        <div className="pt-4 border-t border-gray-100">
          <button
            onClick={() => onView(submission)}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors">
            <Eye className="w-4 h-4" />
            Detayları Görüntüle
          </button>
        </div>
      )}
    </div>
  );
};